import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseVerificationMethodIcon, FlexControlledForm, Inscription  } from '@common-fe/common-fe';

import { MINUTE, SECOND } from '@/common/constants';
import useVerificationMethodsFields, { FormValues } from '@/modules/user/components/SignIn/MultiFactorAuthentication/hooks/useVerificationMethodsFields';
import { MFAStep } from '@/modules/user/components/SignIn/MultiFactorAuthentication/mfa.types';
import useMFAStore from '@/modules/user/components/SignIn/MultiFactorAuthentication/stores/useMFA.store';
import VerificationModal from '@/modules/user/components/Verification/components/VerificationModal';

import RememberMeCheckbox from './RememberMeCheckbox';

const getTimeLeft = (timeStamp?: number) => {
  if (!timeStamp) {
    return 0;
  }

  const timeLeft = Math.round((timeStamp + MINUTE - new Date().getTime()) / SECOND);

  return timeLeft > 0 ? timeLeft : 0;
};

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (step: MFAStep) => void;
  onUpdateTimeout: (timeoutInSeconds: number) => void;
}

const ChangeVerificationMethodModal: React.FC<Props> = ({ onSetVisible, setStep, onUpdateTimeout }) => {
  const { t } = useTranslation();
  const { channels, activeChannel, setActiveChannel } = useMFAStore();
  const fields = useVerificationMethodsFields({ channels, activeChannel });
  const [state, handleSetState] = useState<FormValues>();

  const handleBack = useCallback(() => {
    setStep(MFAStep.SEND_CODE);
  }, [setStep]);

  const handleConfirm = useCallback(() => {
    const newActiveChannel = channels?.find((channel) => channel?.value === state?.verificationMethod);
    if (newActiveChannel) {
      setActiveChannel(newActiveChannel);
      onUpdateTimeout(getTimeLeft(newActiveChannel?.codeSentTimestamp));
    }
    setStep(MFAStep.SEND_CODE);
  }, [ state, channels, setStep, setActiveChannel, onUpdateTimeout]);

  return (
    <VerificationModal
      testId="change-verification-method-modal"
      icon={<ChooseVerificationMethodIcon data-testId="change-verification-method-modal_icon" size="128px" />}
      title={(
        <Inscription
          data-testId="change-verification-method-modal_title"
          margin={{ top: 'spacing12' }}
          size='2xl'
          weight={700}
        >
          {t('Choose Your Verification Method')}
        </Inscription>
      )}
      description={(
        <Inscription
          data-testId="change-verification-method-modal_description"
          margin={{ top: 'spacing12' }}
        >
          {t('Please select how you would like to receive your 6-digit code')}
        </Inscription>
      )}
      onSetVisible={onSetVisible}
      onConfirm={handleConfirm}
      onBack={handleBack}
      form={(
        <FlexControlledForm
          fields={fields}
          onChangeValues={handleSetState}
          formStyle={{ margin: '24px' }}
          editMode
        />
      )}
      footerNode={<RememberMeCheckbox testId="change-verification-method-modal" />}
    />
  );
};

export default ChangeVerificationMethodModal;
